if (!customElements.get('product-extras')) {
  customElements.define(
    'product-extras',
    class ProductExtras extends HTMLElement {
      constructor() {
        super();

        this.checkboxes = Array.from(this.querySelectorAll('[data-cf-extra]'));
        this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
        this.moneySample = this.dataset.moneySample || '';
        // The main price block may show the currency code (theme setting), so it gets its own sample.
        this.priceMoneySample = this.dataset.priceMoneySample || this.moneySample;
        this.breakdownLabel = this.dataset.breakdownLabel || '';
        this.rules = this.parseRules(this.dataset.rules);
        this.exclusiveGroups = this.parseGroups(this.dataset.exclusive);
        this.subtotalEl = this.querySelector('[data-cf-extras-subtotal]');
        this.subtotalAmountEl = this.querySelector('[data-cf-extras-subtotal-amount]');

        this.addEventListener('change', (event) => {
          this.applyExclusivity(event.target);
          this.updateSubtotal();
          this.updateColorFields();
        });
      }

      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.applyRules(event?.data?.variant);
          // Dawn has just re-rendered the price block for the new variant: re-apply the total.
          this.updateSubtotal();
        });
        this.applyRules(this.getCurrentVariant());
        this.updateSubtotal();
        this.updateColorFields();
      }

      // Re-sync subtotal + colour pickers after an external change (e.g. the
      // customizer hiding the colour-adding extras when Pattern = Custom). Hidden
      // rows are excluded by getSelectedCheckboxes(), so this drops their price too.
      refresh() {
        this.updateSubtotal();
        this.updateColorFields();
      }

      // Colour-adding extras: show their colour picker only while the extra is
      // checked & visible. The picker's value is read in getSelectedItems().
      updateColorFields() {
        this.querySelectorAll('[data-cf-extra-row]').forEach((row) => {
          const colorField = row.querySelector('[data-cf-extra-color]');
          if (!colorField) return;
          const checkbox = row.querySelector('[data-cf-extra]');
          const show =
            checkbox &&
            checkbox.checked &&
            !checkbox.disabled &&
            !row.hasAttribute('data-cf-color-off') &&
            !checkbox.closest('[data-cf-hidden], [data-cf-pattern-hidden]');
          colorField.hidden = !show;
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      // Read from this product's own picker (not the event), so a variant change elsewhere
      // on the page (e.g. a quick-add modal) can't feed us another product's price.
      getCurrentVariant() {
        const scope = this.closest('product-info') || document;
        const node = scope.querySelector('variant-selects [data-selected-variant]');
        if (!node) return null;
        try {
          return JSON.parse(node.innerHTML);
        } catch (e) {
          return null;
        }
      }

      // Rules: one per line, "Extra name :: value1, value2". The extra is only
      // shown when the selected variant has one of the listed option values.
      parseRules(raw) {
        if (!raw) return [];
        return raw
          .split(/[\n;]+/)
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [match, values] = line.split('::');
            if (!match || !values) return null;
            return {
              match: match.trim().toLowerCase(),
              values: values
                .split(',')
                .map((value) => value.trim().toLowerCase())
                .filter(Boolean),
            };
          })
          .filter(Boolean);
      }

      // Groups of mutually exclusive extras — one group per line (newline or ";"),
      // names within a line comma-separated. Only lines with 2+ names count. An
      // extra may appear in several lines (e.g. wash conflicts with drips AND
      // hand-painted, while drips and hand-painted don't conflict with each other).
      parseGroups(raw) {
        if (!raw) return [];
        return raw
          .split(/[\n;]+/)
          .map((line) =>
            line
              .split(',')
              .map((name) => name.trim().toLowerCase())
              .filter(Boolean)
          )
          .filter((group) => group.length > 1);
      }

      // When the customer checks an extra, uncheck any other checked extra that
      // shares a group with it, so grouped extras can never be selected together.
      applyExclusivity(changed) {
        if (this.exclusiveGroups.length === 0) return;
        if (!changed || !changed.matches || !changed.matches('[data-cf-extra]') || !changed.checked) return;
        const labelOf = (checkbox) => {
          const item = checkbox.closest('[data-cf-extra-item]');
          return (item ? item.textContent : '').toLowerCase();
        };
        const changedLabel = labelOf(changed);
        this.exclusiveGroups.forEach((group) => {
          if (!group.some((name) => changedLabel.includes(name))) return;
          this.checkboxes.forEach((checkbox) => {
            if (checkbox === changed || !checkbox.checked) return;
            if (group.some((name) => labelOf(checkbox).includes(name))) checkbox.checked = false;
          });
        });
      }

      applyRules(variant) {
        if (this.rules.length === 0) {
          this.updateColorFields();
          return;
        }
        const optionValues = (variant?.options || []).map((value) => String(value).toLowerCase());

        this.checkboxes.forEach((checkbox) => {
          const item = checkbox.closest('[data-cf-extra-item]');
          if (!item) return;
          // Match the rule against the label text only, but hide the whole row so a
          // colour-adding extra's picker hides along with it.
          const row = checkbox.closest('[data-cf-extra-row]') || item;
          const label = item.textContent.toLowerCase();
          const rule = this.rules.find((entry) => label.includes(entry.match));
          if (!rule) return;

          const enabled = rule.values.some((value) => optionValues.includes(value));
          row.toggleAttribute('data-cf-hidden', !enabled);
          if (!enabled && checkbox.checked) checkbox.checked = false;
        });

        this.updateSubtotal();
        this.updateColorFields();
      }

      getSelectedCheckboxes() {
        return this.checkboxes.filter(
          (checkbox) =>
            checkbox.checked &&
            !checkbox.disabled &&
            !checkbox.closest('[data-cf-hidden], [data-cf-pattern-hidden]')
        );
      }

      // Called by product-form.js to add the checked extras as their own line items
      // in the same add-to-cart request. `parentVariantId` is the toy's variant id;
      // passing it as each extra's `parent_id` nests the extra under the toy natively.
      // `configId` is the configured product's shared short code, stamped on each extra so
      // the merchant can see which extras belong to which item when several look identical.
      getSelectedItems(quantity = 1, parentVariantId = '', configId = '') {
        return this.getSelectedCheckboxes().map((checkbox) => {
          const item = { id: checkbox.dataset.variantId, quantity: quantity };
          if (parentVariantId) item.parent_id = parentVariantId;

          const properties = {};
          if (configId) properties._ID = configId;

          // Colour-adding extra: attach the chosen colour to this extra's own line
          // (unless its picker is switched off, e.g. on the Custom pattern).
          const row = checkbox.closest('[data-cf-extra-row]');
          const colorInput =
            row &&
            !row.hasAttribute('data-cf-color-off') &&
            row.querySelector('[data-cf-extra-color] [data-cf-color-input]');
          if (colorInput && colorInput.value) properties.Colour = colorInput.value;

          if (Object.keys(properties).length > 0) item.properties = properties;
          return item;
        });
      }

      updateSubtotal() {
        const cents = this.getSelectedCheckboxes().reduce(
          (sum, checkbox) => sum + (parseInt(checkbox.dataset.price, 10) || 0),
          0
        );

        this.updateProductPrice(cents);
        if (!this.subtotalEl) return;

        if (cents > 0) {
          this.subtotalEl.hidden = false;
          if (this.subtotalAmountEl) this.subtotalAmountEl.textContent = this.formatMoney(cents);
        } else {
          this.subtotalEl.hidden = true;
        }
      }

      // Make the product's main price show what the customer will actually pay: variant
      // price + selected extras (incl. the auto-added surcharges), with a small
      // "base + extras" line under it. Dawn rewrites #price-<section> on every variant
      // change, so this is re-applied from scratch each time and never stacks; the
      // original text is kept on each element to restore when no extras are selected.
      updateProductPrice(cents) {
        if (!this.hasAttribute('data-price-total')) return;
        const priceEl = document.getElementById(`price-${this.dataset.sectionId}`);
        const variant = this.getCurrentVariant();
        if (!priceEl || !variant) return;

        const setPrice = (selector, baseCents) => {
          priceEl.querySelectorAll(selector).forEach((el) => {
            if (el.dataset.cfBaseText === undefined) el.dataset.cfBaseText = el.textContent;
            el.textContent =
              cents > 0 ? this.formatMoney(baseCents + cents, this.priceMoneySample) : el.dataset.cfBaseText;
          });
        };
        setPrice('.price__regular .price-item--regular, .price__sale .price-item--sale', variant.price);
        // On sale, add the extras to the struck-through price too, so the discount isn't inflated.
        if (variant.compare_at_price > variant.price) {
          setPrice('.price__sale s.price-item--regular', variant.compare_at_price);
        }

        let breakdown = priceEl.querySelector('[data-cf-price-breakdown]');
        if (cents > 0) {
          if (!breakdown) {
            breakdown = document.createElement('p');
            breakdown.className = 'cf-price-breakdown';
            breakdown.setAttribute('data-cf-price-breakdown', '');
            priceEl.appendChild(breakdown);
          }
          breakdown.textContent =
            `${this.formatMoney(variant.price)} + ${this.formatMoney(cents)} ${this.breakdownLabel}`.trim();
        } else if (breakdown) {
          breakdown.remove();
        }
      }

      // Format cents like Liquid's money filter in the customer's currency: take the
      // rendered 1,000,000.00 sample and swap its digits for ours, keeping the symbol,
      // separators and number of decimals. Falls back to shop.money_format.
      formatMoney(cents, sample = this.moneySample) {
        const match = sample.match(/1(?<sep>\D?)000\k<sep>000(?:(\D)(\d{1,3}))?/);
        if (!match) return this.formatWithMoneyFormat(cents);
        const [number, thousands, decimal, fraction = ''] = match;
        const [whole, part] = (cents / 100).toFixed(fraction.length).split('.');
        const digits = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (part ? decimal + part : '');
        return sample.replace(number, () => digits);
      }

      formatWithMoneyFormat(cents) {
        const value = cents / 100;
        const withDelimiters = (precision, thousands = ',', decimal = '.') => {
          const parts = value.toFixed(precision).split('.');
          const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          return whole + (parts[1] ? decimal + parts[1] : '');
        };

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => {
          switch (name) {
            case 'amount_no_decimals':
              return withDelimiters(0);
            case 'amount_with_comma_separator':
              return withDelimiters(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(0, '.', ',');
            case 'amount_with_apostrophe_separator':
              return withDelimiters(2, "'", '.');
            default:
              return withDelimiters(2);
          }
        });
      }
    }
  );
}
